import React, { useState } from "react";
import styled from "@emotion/styled";
import { useGetMatchProgressTakers } from "@src/hooks/queries/useAgency";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    matchNo: number | null;
    takerType: "request" | "view" | "push" | null;
    count: number;
    serviceName: string;
    clientName: string;
}

const renderMatchRequestStatus = (status: number) => {
    switch (status) {
        case 0:
            return <span className="gray-text">미지원</span>;
        case 1:
            return <span>지원 (매칭 대기 중)</span>;
        case 2:
            return <span className="blue-text">매칭 확정</span>;
        case 3:
            return <span className="red-text">매칭 실패</span>;
        default:
            return <span className="gray-text">미지원</span>;
    }
};

const SupportStatusModal = ({ isOpen, onClose, matchNo, takerType, count, serviceName, clientName }: Props) => {
    const [page, setPage] = useState(1);
    const limit = 10;

    const { data } = useGetMatchProgressTakers(
        {
            matchNo: matchNo || 0,
            takerType: takerType || "request",
            page,
            limit,
        },
        isOpen && matchNo !== null && takerType !== null,
    );

    if (!isOpen) return null;

    const experts = data?.experts || [];
    const totalCount = data?.totalCount || 0;

    const getModalTitle = () => {
        if (takerType === "request") return "전문가 매칭 진행 상태";
        if (takerType === "view") return "의뢰 열람한 전문가";
        if (takerType === "push") return "알림 수신 전문가";
        return "전문가 매칭 진행 상태";
    };

    const getRightBoxTitle = () => {
        if (takerType === "request") return "지원한 전문가";
        if (takerType === "view") return "의뢰 열람한 전문가";
        if (takerType === "push") return "알림 수신 전문가";
        return "지원한 전문가";
    };

    return (
        <Overlay onClick={onClose}>
            <ModalContainer onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{getModalTitle()}</h2>
                    <button className="close-btn" onClick={onClose}>
                        ✕
                    </button>
                </div>

                <div className="modal-summary">
                    <div className="summary-card">
                        <div>
                            서비스 : <strong>{serviceName}</strong>
                        </div>
                        <div>
                            의뢰인 : <strong>{clientName}</strong>
                        </div>
                    </div>
                    <div className="summary-card center">
                        <div className="stat">
                            <span>{getRightBoxTitle()}</span>
                            <strong>{totalCount}</strong>
                        </div>
                    </div>
                </div>

                <div className="modal-table">
                    <table>
                        <thead>
                            <tr>
                                <th>회원번호</th>
                                <th>이름</th>
                                <th>전문가 유형</th>
                                <th>이메일</th>
                                <th>전화번호</th>
                                <th>상태</th>
                            </tr>
                        </thead>
                        <tbody>
                            {experts.map((expert, index) => (
                                <tr key={expert.takerUserNo || index}>
                                    <td>{expert.takerUserNo}</td>
                                    <td>
                                        <a href="#">{expert.takerUserName}</a>
                                    </td>
                                    <td>{(expert.counselingCategories || []).join(", ")}</td>
                                    <td>{expert.takerEmail}</td>
                                    <td>{expert.takerPhone}</td>
                                    <td>{renderMatchRequestStatus(expert.matchRequestStatus)}</td>
                                </tr>
                            ))}
                            {experts.length === 0 && (
                                <tr>
                                    <td colSpan={6}>내역이 없습니다.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="modal-pagination">
                    <button className="arrow" onClick={() => setPage(Math.max(page - 1, 1))}>
                        {"<"}
                    </button>
                    <button className="page active">{page}</button>
                    <button className="arrow" onClick={() => setPage(page + 1)}>
                        {">"}
                    </button>
                </div>
            </ModalContainer>
        </Overlay>
    );
};

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
    z-index: 1000;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const ModalContainer = styled.div`
    background-color: #fff;
    width: 1200px;
    max-width: 90vw;
    border-radius: 8px;
    padding: 32px;
    position: relative;
    max-height: 90vh;
    overflow-y: auto;

    .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 24px;

        h2 {
            font-size: 18px;
            font-weight: 600;
        }

        .close-btn {
            background: none;
            border: none;
            font-size: 24px;
            cursor: pointer;
            color: #666;
        }
    }

    .modal-summary {
        display: flex;
        gap: 16px;
        margin-bottom: 24px;

        .summary-card {
            flex: 1;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 24px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: 12px;
            font-size: 14px;
            color: #374151;

            strong {
                font-weight: 600;
                color: #111827;
            }

            &.center {
                flex-direction: row;
                justify-content: space-around;
                .stat {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 8px;

                    strong {
                        font-size: 20px;
                    }
                }
            }
        }
    }

    .modal-table {
        table {
            width: 100%;
            border-collapse: collapse;
            text-align: center;

            th,
            td {
                padding: 16px 12px;
                font-size: 14px;
                border-bottom: 1px solid #e5e7eb;
            }

            th {
                color: #6b7280;
                font-weight: 500;
            }

            a {
                color: #111827;
                text-decoration: underline;
                text-underline-offset: 4px;
            }

            .blue-text {
                color: #2563eb;
                font-weight: 500;
            }
            .red-text {
                color: #dc2626;
                font-weight: 500;
            }
            .gray-text {
                color: #9ca3af;
                font-weight: 500;
            }
        }
    }

    .modal-pagination {
        display: flex;
        justify-content: center;
        align-items: center;
        margin-top: 24px;
        gap: 8px;

        button {
            width: 32px;
            height: 32px;
            border: 1px solid #e5e7eb;
            background: white;
            border-radius: 4px;
            cursor: pointer;
            color: #6b7280;

            &.active {
                border-color: #3b82f6;
                color: #3b82f6;
                background-color: #eff6ff;
            }
        }
    }
`;

export default SupportStatusModal;
